import React, { useState, useEffect } from "react";
import axios from "axios";
import { Send, CheckCircle2, XCircle, Loader2, ArrowLeft, LogOut } from "lucide-react";
import { useAuth } from "./AuthContext";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Admin = () => {
  const { user, logout, authHeader } = useAuth();
  const [recipientsRaw, setRecipientsRaw] = useState("");
  const [preview, setPreview] = useState(null);
  const [previewOpen, setPreviewOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios
      .get(`${API}/newsletter/preview`)
      .then((r) => setPreview(r.data))
      .catch(() => setError("Vorschau konnte nicht geladen werden."));
  }, []);

  const parseEmails = (raw) => {
    const parts = raw
      .split(/[\s,;\n]+/)
      .map((s) => s.trim())
      .filter(Boolean);
    return Array.from(new Set(parts.map((p) => p.toLowerCase())));
  };

  const emails = parseEmails(recipientsRaw);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validEmails = emails.filter((e) => emailRegex.test(e));
  const invalidEmails = emails.filter((e) => !emailRegex.test(e));

  const send = async (dryRun = false) => {
    setLoading(true);
    setResult(null);
    setError("");
    try {
      const r = await axios.post(`${API}/newsletter/send`, {
        recipients: validEmails,
        dry_run: dryRun,
      }, { headers: authHeader() });
      setResult(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Unbekannter Fehler.");
    } finally {
      setLoading(false);
    }
  };

  const canSend = validEmails.length > 0 && !loading;

  return (
    <div className="min-h-screen bg-ivory-100 text-ink-900 font-sans">
      {/* Header */}
      <header className="bg-ink-900 text-ivory-100 border-b border-gold-500/30">
        <div className="max-w-6xl mx-auto px-6 py-5 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-9 h-9 rounded-full bg-ink-900 border border-gold-500 flex items-center justify-center">
              <span className="text-gold-500 font-display font-semibold text-sm">M</span>
            </div>
            <div>
              <div className="font-display font-medium text-[15px]">Moonshine · Newsletter Admin</div>
              <div className="text-[11px] uppercase tracking-[0.18em] text-gold-300">Interner Bereich · {user?.email}</div>
            </div>
          </div>
          <div className="flex items-center gap-5">
            <a href="/backoffice" data-testid="admin-backoffice-link" className="text-sm text-ivory-100/70 hover:text-gold-300 transition">Backoffice</a>
            <button onClick={logout} data-testid="admin-logout" className="inline-flex items-center gap-1.5 text-sm text-ivory-100/70 hover:text-gold-300 transition">
              <LogOut className="w-4 h-4" /> Abmelden
            </button>
            <a
              href="/"
              data-testid="admin-back-link"
              className="inline-flex items-center gap-2 text-sm text-ivory-100/70 hover:text-gold-300 transition"
            >
              <ArrowLeft className="w-4 h-4" /> Zur Seite
            </a>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto px-6 py-10 grid lg:grid-cols-5 gap-8">
        {/* Left: form */}
        <div className="lg:col-span-2 space-y-6">
          <div className="bg-white rounded-2xl p-6 border border-ink-700/10 shadow-sm">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-display text-lg font-medium">Empfänger</h2>
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  data-testid="load-audience-btn"
                  onClick={async () => {
                    try {
                      const res = await fetch(`${(process.env.REACT_APP_BACKEND_URL || "")}/api/newsletter/audience-contacts`, {
                        method: "POST",
                        headers: {"Content-Type": "application/json", ...authHeader()},
                      });
                      const data = await res.json();
                      if (!res.ok) throw new Error(data.detail || "Fehler");
                      const existing = new Set(recipientsRaw.split(/[\s,;]+/).map(s => s.trim().toLowerCase()).filter(Boolean));
                      (data.emails || []).forEach(e => existing.add(e.toLowerCase()));
                      setRecipientsRaw([...existing].join("\n"));
                    } catch (e) {
                      alert("Konnte Audience nicht laden: " + e.message);
                    }
                  }}
                  className="text-xs bg-gold-500 hover:bg-gold-600 text-ink-900 px-3 py-1.5 rounded-full font-medium transition"
                >
                  Aus Resend-Audience laden
                </button>
                <div className="text-xs text-ink-500">
                  <span className="text-ink-900 font-semibold">{validEmails.length}</span> gültig
                  {invalidEmails.length > 0 && (
                    <span className="text-red-600 ml-2">· {invalidEmails.length} ungültig</span>
                  )}
                </div>
              </div>
            </div>
            <textarea
              data-testid="recipients-textarea"
              value={recipientsRaw}
              onChange={(e) => setRecipientsRaw(e.target.value)}
              placeholder="Emails hier reinpasten (durch Komma, Semikolon, Leerzeichen oder Zeilenumbruch getrennt)&#10;&#10;beispiel1@mail.com&#10;beispiel2@mail.com"
              rows={9}
              className="w-full px-4 py-3 rounded-xl border border-ink-700/15 bg-ivory-50 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-gold-500/40 resize-none"
            />
            {invalidEmails.length > 0 && (
              <div className="mt-3 text-xs text-red-600" data-testid="invalid-emails-warning">
                Ungültig: {invalidEmails.slice(0, 3).join(", ")}
                {invalidEmails.length > 3 ? " …" : ""}
              </div>
            )}
          </div>

          <div className="bg-ink-900 text-ivory-100 rounded-2xl p-6 ring-1 ring-gold-500/25">
            <div className="text-xs uppercase tracking-widest text-gold-300 mb-1">Betreff</div>
            <div className="font-display text-base mb-5 leading-snug">
              {preview?.subject || "Wird geladen…"}
            </div>

            <button
              onClick={() => send(false)}
              disabled={!canSend}
              data-testid="send-newsletter-button"
              className="w-full inline-flex items-center justify-center gap-2 rounded-full bg-gold-500 hover:bg-gold-400 disabled:bg-ink-500 disabled:cursor-not-allowed text-ink-900 px-6 py-4 text-base font-medium transition-all"
            >
              {loading ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Wird versendet …
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  An {validEmails.length || 0} Empfänger senden
                </>
              )}
            </button>

            <button
              onClick={() => send(true)}
              disabled={!canSend}
              data-testid="dry-run-button"
              className="mt-2 w-full text-xs text-ivory-100/60 hover:text-gold-300 transition"
            >
              Test-Modus (Dry-Run, sendet nichts)
            </button>
          </div>

          {error && (
            <div className="bg-red-50 border border-red-200 text-red-800 rounded-xl px-4 py-3 text-sm" data-testid="error-message">
              <div className="flex gap-2">
                <XCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
                <div>{error}</div>
              </div>
            </div>
          )}

          {result && (
            <div className="bg-white rounded-2xl border border-ink-700/10 p-6 shadow-sm" data-testid="result-panel">
              <div className="flex items-center gap-2 mb-4">
                <CheckCircle2 className="w-5 h-5 text-gold-600" />
                <h3 className="font-display text-lg font-medium">Ergebnis</h3>
              </div>
              <div className="grid grid-cols-3 gap-3 mb-4">
                <div className="text-center p-3 rounded-xl bg-ivory-100">
                  <div className="font-display text-2xl text-ink-900">{result.total}</div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-500 mt-1">Total</div>
                </div>
                <div className="text-center p-3 rounded-xl bg-ivory-100">
                  <div className="font-display text-2xl text-gold-600" data-testid="result-sent-count">{result.sent}</div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-500 mt-1">Gesendet</div>
                </div>
                <div className="text-center p-3 rounded-xl bg-ivory-100">
                  <div className={`font-display text-2xl ${result.failed > 0 ? "text-red-600" : "text-ink-500"}`} data-testid="result-failed-count">
                    {result.failed}
                  </div>
                  <div className="text-[10px] uppercase tracking-widest text-ink-500 mt-1">Fehler</div>
                </div>
              </div>

              <div className="max-h-64 overflow-y-auto space-y-1.5 text-xs">
                {result.results.map((r, i) => (
                  <div key={i} className="flex items-center justify-between gap-2 px-3 py-2 rounded-lg bg-ivory-50">
                    <span className="font-mono truncate">{r.email}</span>
                    {r.status === "sent" ? (
                      <span className="text-gold-700 flex items-center gap-1 flex-shrink-0">
                        <CheckCircle2 className="w-3.5 h-3.5" /> ok
                      </span>
                    ) : (
                      <span className="text-red-600 flex items-center gap-1 flex-shrink-0" title={r.error}>
                        <XCircle className="w-3.5 h-3.5" /> Fehler
                      </span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Right: preview */}
        <div className="lg:col-span-3">
          <div className="bg-white rounded-2xl border border-ink-700/10 overflow-hidden shadow-sm">
            <div className="flex items-center justify-between px-5 py-3 border-b border-ink-700/10 bg-ivory-100">
              <div className="font-display text-sm font-medium">Vorschau</div>
              <button
                onClick={() => setPreviewOpen(!previewOpen)}
                className="text-xs text-ink-500 hover:text-ink-900"
                data-testid="toggle-preview-button"
              >
                {previewOpen ? "Ausblenden" : "Anzeigen"}
              </button>
            </div>
            {previewOpen && preview && (
              <iframe
                title="email-preview"
                data-testid="email-preview-iframe"
                srcDoc={preview.html}
                className="w-full"
                style={{ height: "820px", border: "0" }}
              />
            )}
            {previewOpen && !preview && (
              <div className="p-10 text-center text-ink-500 text-sm">Vorschau lädt …</div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Admin;
